import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Admin upload for the compiled copier EA.
//
// The .mq5 source ships with the frontend, but most users will not open MetaEditor, so the
// default download is a compiled .ex5. It cannot be built here — MetaEditor is a Windows
// desktop application — so the operator compiles it and uploads the binary.
//
// Stored on the BACKEND, not in the frontend's public folder: the frontend is a separate build
// artefact on this host, so a file dropped into public/ would disappear on the next deploy.
function CopierEaUploader() {
  const token = localStorage.getItem('adminToken');
  const [meta, setMeta] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/signals/copier/ea-meta`);
      setMeta(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const form = new FormData();
      form.append('ea', file);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/signals/copier/ea`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMeta(data);
      setFile(null);
      setNotice('Uploaded. Users now get this build from the Signals tab.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-ea-upload">
      <h2>MT5 copier EA</h2>
      <p className="admin-ea-intro">
        Users download this compiled build instead of compiling the source themselves. Rebuild
        and re-upload whenever <code>VixSignalCopier.mq5</code> changes — the source and the
        binary are not linked, so an old .ex5 will silently keep the old behaviour.
      </p>

      {error && <p className="admin-ea-error">{error}</p>}
      {notice && <p className="admin-ea-notice">{notice}</p>}

      <div className="admin-ea-card">
        <h3>Current build</h3>

        {!meta ? (
          <p>Loading…</p>
        ) : meta.available ? (
          <span className="admin-ea-status present">
            Uploaded {new Date(meta.uploadedAt).toLocaleString()} ·{' '}
            {(meta.sizeBytes / 1024).toFixed(0)} KB
          </span>
        ) : (
          <span className="admin-ea-status">
            Nothing uploaded — users see the .mq5 source with compile instructions
          </span>
        )}

        <ol className="admin-ea-steps">
          <li>
            Open <code>mt5/VixSignalCopier.mq5</code> in MetaEditor and press <strong>F7</strong>.
            {' '}Compile from the terminal&apos;s own data folder, not Program Files — MetaEditor
            cannot write the .ex5 there.
          </li>
          <li>
            Find <code>VixSignalCopier.ex5</code> beside the source and select it here.
          </li>
          <li>
            <input
              className="admin-ea-file"
              type="file"
              accept=".ex5"
              onChange={(event) => { setFile(event.target.files?.[0] || null); setNotice(''); }}
            />
            <button
              type="button"
              className="admin-ea-submit"
              disabled={busy || !file}
              onClick={submit}
            >
              {busy ? 'Uploading…' : 'Upload .ex5'}
            </button>
          </li>
        </ol>
      </div>
    </div>
  );
}

export default CopierEaUploader;
